import React, { FormEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { ApolloClient, useMutation, useQuery } from '@apollo/client'
import { useElements, useStripe } from '@stripe/react-stripe-js'
import { deleteCookie, getCookie } from 'cookies-next'
import noop from 'lodash/noop'
import { v4 as uuid } from 'uuid'
import { paths } from '@/constants/paths'
import { generateRecaptchaToken } from '@/services/Captcha/Captcha'
import {
  GET_SAVED_PAYMENT_INFO_QUERY,
  GUEST_STRIPE_PURCHASE_MUTATION,
  GuestPurchaseRequest,
  PurchaseRequest,
  STRIPE_MONTHLY_SUBSCRIPTION_MUTATION,
  STRIPE_ONE_TIME_PURCHASE_MUTATION,
} from '@/services/PifCheckoutService'
import { Offer } from '@/services/PifService'
import { getPriceForCurrency } from '@/services/PriceFormatter'
import { User } from '@/services/UserService'
import {
  DoStripeMonthlySubscriptionMutation,
  DoStripeOneTimePurchaseMutation,
  DoStripeOneTimeGuestCheckoutMutation,
  GetSavedPaymentInfoQuery,
  MoneyCurrencyCode,
} from '@/types/codegen-federation'
import { ReactFCC } from '@/types/react'
import { getFormErrorMessage } from '@/utils/StripeFormValidator'
import { useSafeTrack } from '@/utils/analytics'
import { OrderCompletedPayload, useEcommerceEvents } from '@/utils/analytics/ecommerce-events'
import { reportErrorToBugsnag } from '@/utils/bugsnag'
import { cLogger } from '@/utils/logging/client-logger'
import { getUserDisplayName } from '@/utils/users'
import { getPaymentMethod } from './PayItForwardHelper'
import { PaymentMethod } from './PayItForwardHelper/PayItForwardHelper'

interface OnCompleteProps {
  orderId: string
  token?: string | null
  email?: string | null
}

interface PayItForwardFormProps {
  onComplete: (props: OnCompleteProps) => void
  offer: Offer
  projectSlug: string
  isSubscription: boolean
  bimClient?: ApolloClient<object>
  user?: User | null
  guildMembershipIncluded?: boolean
}

interface PayItForwardFormState {
  name: string
  setName: (name: string) => void
  email: string
  setEmail: (email: string) => void
  errorMessage?: string
  isSubmitting: boolean
  savedPaymentInfo?: GetSavedPaymentInfoQuery['paymentInfo']
  setSavedPaymentInfo: (paymentInfo?: GetSavedPaymentInfoQuery['paymentInfo']) => void
  checkout: (e: FormEvent<HTMLFormElement>) => Promise<void>
  setSubmitting: (isSubmitting: boolean) => void
  onPayItForwardComplete: (args: OrderCompleteArgs) => void
  setErrorMessage: (error: string) => void
  bimClient?: ApolloClient<object>
  guildMembershipIncluded?: boolean
}

interface HandleErrorProps {
  checkoutId: string
  errorMessage?: string
}

export interface OrderCompleteArgs {
  orderId: string
  paymentMethod?: string | null
  token?: string | null
  email?: string | null
}

const PayItForwardFormContext = React.createContext<PayItForwardFormState>({
  name: '',
  setName: noop,
  email: '',
  setEmail: noop,
  errorMessage: undefined,
  isSubmitting: false,
  setSubmitting: noop,
  savedPaymentInfo: null,
  setSavedPaymentInfo: noop,
  checkout: () => Promise.resolve(),
  onPayItForwardComplete: noop,
  setErrorMessage: noop,
  bimClient: undefined,
  guildMembershipIncluded: false,
})

export const PayItForwardFormProvider: ReactFCC<PayItForwardFormProps> = ({
  children,
  onComplete,
  offer,
  projectSlug,
  isSubscription,
  user,
  bimClient,
  guildMembershipIncluded = false,
}) => {
  const track = useSafeTrack()
  const { trackOrderCompleted: trackOrderCompletedEvent } = useEcommerceEvents()
  const stripe = useStripe()
  const elements = useElements()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setSubmitting] = useState(false)
  const [savedPaymentInfo, setSavedPaymentInfo] = useState<GetSavedPaymentInfoQuery['paymentInfo']>()
  const [guestPurchaseMutation] = useMutation<DoStripeOneTimePurchaseMutation>(GUEST_STRIPE_PURCHASE_MUTATION, {
    client: bimClient,
    errorPolicy: 'all',
  })
  const [purchaseMutation] = useMutation<DoStripeOneTimePurchaseMutation>(STRIPE_ONE_TIME_PURCHASE_MUTATION, {
    client: bimClient,
    errorPolicy: 'all',
  })
  const [subscribeMutation] = useMutation<DoStripeMonthlySubscriptionMutation>(STRIPE_MONTHLY_SUBSCRIPTION_MUTATION, {
    errorPolicy: 'all',
  })
  const { data: savedPaymentData } = useQuery<GetSavedPaymentInfoQuery>(GET_SAVED_PAYMENT_INFO_QUERY, {
    skip: !user,
    errorPolicy: 'all',
  })

  useEffect(() => {
    setName(getUserDisplayName(user))
    setEmail(user?.email ?? '')
  }, [user])

  useEffect(() => {
    if (savedPaymentData?.paymentInfo) {
      setSavedPaymentInfo(savedPaymentData?.paymentInfo)
    }
  }, [savedPaymentData])

  const handleError = useCallback(
    ({ checkoutId, errorMessage = 'Please check your payment method.' }: HandleErrorProps) => {
      setErrorMessage(errorMessage)
      setSubmitting(false)
      const trackingPayload = {
        category: 'pif',
        currency: offer.currency,
        region: offer.region,
        project: projectSlug,
        products: [
          {
            product_id: offer.id,
            sku: offer.slug,
            category: 'pif',
            name: offer.name,
            price: offer.price,
            currency: offer.currency,
            region: offer.region,
            quantity: 1,
            url: `${paths.base}${paths.payItForward.index}/${projectSlug}/checkout/${offer.id}`,
          },
        ],
        subscription: isSubscription,
        checkout_id: checkoutId,
      }
      track('Checkout Failed', trackingPayload)
      const err = new Error(errorMessage)
      reportErrorToBugsnag(Object.assign(err, trackingPayload))
    },
    [isSubscription, offer.currency, offer.id, offer.name, offer.price, offer.slug, offer.region, projectSlug, track],
  )

  const prepareForCheckout = useCallback(
    (basePurchaseRequest: PurchaseRequest, googleCaptchaToken: string | undefined) => {
      if (isSubscription) {
        return { checkoutMutation: subscribeMutation, purchaseRequest: basePurchaseRequest }
      } else if (!user) {
        return {
          checkoutMutation: guestPurchaseMutation,
          purchaseRequest: {
            ...basePurchaseRequest,
            guestCheckout: true,
            email,
            googleCaptchaToken,
          } as GuestPurchaseRequest,
        }
      } else {
        return {
          checkoutMutation: purchaseMutation,
          purchaseRequest: {
            ...basePurchaseRequest,
          },
        }
      }
    },
    [email, guestPurchaseMutation, isSubscription, purchaseMutation, subscribeMutation, user],
  )

  const trackOrderCompleted = useCallback(
    ({ orderId, paymentMethod }: OrderCompleteArgs) => {
      const checkoutId = (getCookie('pif_checkout_id') ?? uuid()) as string
      const trackPayload: OrderCompletedPayload = {
        funnel: 'pif',
        category: 'pif',
        currency: offer.currency as MoneyCurrencyCode,
        email,
        region: offer.region,
        project: projectSlug,
        products: [
          {
            product_id: offer.id,
            sku: guildMembershipIncluded ? `${offer.slug}-guild` : offer.slug,
            category: 'pif',
            name: offer.name,
            price: getPriceForCurrency(offer.price, offer.currency),
            currency: offer.currency as MoneyCurrencyCode,
            region: offer.region,
            quantity: 1,
            url: `${paths.base}${paths.payItForward.index}/${projectSlug}/checkout/${offer.id}`,
          },
        ],
        subscription: isSubscription,
        checkout_id: checkoutId,
        order_id: orderId,
        payment_method: paymentMethod,
        revenue: getPriceForCurrency(offer.price, offer.currency),
        shipping: 0,
        tax: 0,
        total: getPriceForCurrency(offer.price, offer.currency),
      }

      trackOrderCompletedEvent(trackPayload)
      track(`Order Completed ${projectSlug}`, trackPayload)

      if (isSubscription) {
        track('Subscription Order Completed', trackPayload)
      }
    },
    [
      email,
      offer.region,
      isSubscription,
      offer.currency,
      offer.id,
      offer.name,
      offer.price,
      offer.slug,
      projectSlug,
      track,
      trackOrderCompletedEvent,
      guildMembershipIncluded,
    ],
  )

  const onPayItForwardComplete = useCallback(
    ({ orderId, paymentMethod, token, email }: OrderCompleteArgs) => {
      trackOrderCompleted({ orderId, paymentMethod })
      onComplete?.({ orderId, token, email })
    },
    [onComplete, trackOrderCompleted],
  )

  const checkout = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      if (isSubmitting) return

      const formError = getFormErrorMessage({ name, email })
      setErrorMessage(formError)
      if (formError) return

      setSubmitting(true)

      // skip in unit test since it's a bot testing
      let paymentMethod
      const checkoutId = (getCookie('pif_checkout_id') ?? uuid()) as string

      try {
        const googleCaptchaToken = await generateRecaptchaToken('checkout')
        paymentMethod = await getPaymentMethod({ savedPaymentInfo, elements, stripe, name })

        if (paymentMethod.errorMessage) {
          handleError({ checkoutId, errorMessage: paymentMethod.errorMessage })
          cLogger().error('payment method error', paymentMethod.errorMessage)
        } else {
          const { checkoutMutation, purchaseRequest } = prepareForCheckout(
            {
              offerSlug: guildMembershipIncluded ? `${offer.slug}-guild` : offer.slug,
              stripeToken: paymentMethod.stripeToken,
            },
            googleCaptchaToken,
          )

          const resp = await checkoutMutation({
            variables: { input: purchaseRequest },
          })

          if (resp?.errors && resp.errors.length > 0) {
            const errorMessage = resp.errors?.[0]?.message
            setSubmitting(false)
            setErrorMessage(errorMessage)
            return
          }

          if (resp.data) {
            onPayItForwardComplete(resolvePifMutationResponse(resp.data, paymentMethod))
          }

          deleteCookie('pif_checkout_id')
        }
      } catch (e) {
        cLogger().error('An error occurred dureing PIF checkout', e)
        reportErrorToBugsnag(e as Error)
        handleError({ checkoutId })
      }
    },
    [
      isSubmitting,
      name,
      email,
      savedPaymentInfo,
      elements,
      stripe,
      handleError,
      prepareForCheckout,
      offer.slug,
      onPayItForwardComplete,
      guildMembershipIncluded,
    ],
  )

  const value = useMemo(
    () => ({
      name,
      setName,
      email,
      setEmail,
      errorMessage,
      isSubmitting,
      setSubmitting,
      savedPaymentInfo,
      setSavedPaymentInfo,
      checkout,
      onPayItForwardComplete,
      setErrorMessage,
      bimClient,
      guildMembershipIncluded,
    }),
    [
      name,
      setName,
      email,
      setEmail,
      errorMessage,
      isSubmitting,
      setSubmitting,
      savedPaymentInfo,
      setSavedPaymentInfo,
      checkout,
      onPayItForwardComplete,
      setErrorMessage,
      bimClient,
      guildMembershipIncluded,
    ],
  )

  return <PayItForwardFormContext.Provider value={value}>{children}</PayItForwardFormContext.Provider>
}

export const usePayItForwardForm = () => {
  return React.useContext(PayItForwardFormContext)
}

function resolvePifMutationResponse(
  data: DoStripeOneTimePurchaseMutation | DoStripeMonthlySubscriptionMutation | DoStripeOneTimeGuestCheckoutMutation,
  paymentMethod: PaymentMethod,
) {
  let orderId = ''
  let userToken: string | null | undefined
  let userEmail: string | null | undefined

  if ('stripePurchase' in data) {
    orderId = data?.stripePurchase?.order?.id ?? ''
  }

  if ('stripeSubscription' in data) {
    orderId = data?.stripeSubscription?.subscription?.id ?? ''
  }

  if ('guestStripePurchase' in data) {
    const isGuestCheckout = data?.guestStripePurchase?.userCreatedWithOrder
    userToken = isGuestCheckout ? data?.guestStripePurchase?.userToken : undefined
    userEmail = data?.guestStripePurchase?.order?.user?.email
  }

  return { orderId, paymentMethod: paymentMethod.brand as string, token: userToken, email: userEmail }
}
